import { useFormik } from 'formik'
import * as store from '@store'
import * as halfileActions from '@actions/halfile'
import SearchFormSidebar from '../Collection/SearchFormSidebar'
import { TAB_SEARCH_INDEX_KEY } from '../Collection/useCollectionViewModel'
import { useCallback, useEffect, useState } from 'react'
import cn from 'clsx'
import CircleSpinner from '@/components/shared/CircleSpinner'
import { useParams } from 'react-router-dom'
import { useWireValue } from '@forminator/react-wire'
import Title from '@/components/shared/Title'
import { MdBusiness, MdCollectionsBookmark } from 'react-icons/md'
import { formatDate } from '@/utils/date'
import { DocumentCountySearchTable } from './DocumentCountySearchTable'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

const initialValues = {
    grantor: '',
    grantee: '',
    grantorGrantee: '',
    county: '',
    documentNumber: '',
    bookType: '',
    bookVolume: '',
    bookPage: '',
    instrumentDateFrom: '',
    instrumentDateTo: '',
    fileDateFrom: '',
    fileDateTo: '',
    documentType: '',
    allLegalDescriptions: '',
}

export const HalfileCountySearch = () => {
    
    const { countyKey } = useParams()
    
    const [loading, setLoading] = useState(false)
    const [documents, setDocuments] = useState([])
    const currentCounty = useWireValue(store.currentCounty)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const { handleSubmit, handleChange, handleReset } = useFormik({
        initialValues: initialValues,
        onSubmit: async formValues => {
            if (!Object.values(formValues).filter(Boolean).length) {
                return toast.warn('Add filters to search')
            }
            
            setLoading(true)
            
            try {
                const docs = await halfileActions.findDocumentsByFilter(countyKey, formValues)
                
                setDocuments(docs)
            } catch (err) {
                toast.error('Failed to search documents by the given filter')
            }
            
            setLoading(false)
        },
    })
    
    const handleFormReset = e => {
        
        handleReset(e)
        
        // @todo there is a better way to handle this
        // eslint-disable-next-line no-restricted-globals
        const form = document.searchForm
        
        // had to manually set the values otherwise they wouldn't disappear on the screen
        // as the inputs are uncontrolled
        // also, form.reset didn't work for this
        Object.keys(initialValues).forEach(k => {
            if (form.elements[k]) {
                form.elements[k].value = initialValues[k]
                
                // flatpickr wouldn't allow me just to reset the value, so had to force it :)
                if (form.elements[k]._flatpickr)
                    form.elements[k]._flatpickr.setDate(initialValues[k])
            }
        })
        
        setDocuments([])
    }
    
    const handleKeyUp = useCallback(e => {
        if (e.key !== 'Enter') return
        e.preventDefault()
        e.stopPropagation()
        return handleSubmit(e)
    }, [handleSubmit])
    
    useEffect(() => {
        halfileActions.fetchCountyByKey(countyKey)
    }, [countyKey])
    
    if (!remoteConfig?.halfile?.enabled)
        return null
    
    return (
        <div>
            <Title>{currentCounty?.name + ' County'}</Title>
            
            <header className="flex justify-between px-4 mb-3">
                <div className="text-sm breadcrumbs">
                    <ul>
                        <li><Link to="/halfile">CountyRecords.com Collections</Link></li>
                        <li>{currentCounty?.name + ' County'}</li>
                    </ul>
                </div>
                <div className="flex flex-col pr-2">
                    <div className="flex justify-end items-center gap-2">
                        <span>{currentCounty?.name + ' County'}</span>
                        <MdCollectionsBookmark className="text-xl" />
                        <span className="tooltip tooltip-primary tooltip-left"
                            data-tip="This is a CountyRecords.com County collection">
                            <MdBusiness className="text-xl" />
                        </span>
                    </div>
                    {currentCounty?.coverageStartDate 
                    && currentCounty?.coverageEndDate && (
                        <div
                            className="text-muted text-right text-sm tooltip tooltip-primary tooltip-left"
                            data-tip="Certification Date">
                            {formatDate(currentCounty?.coverageStartDate, 'MM/DD/YYYY')}
                            {' '} to {' '}
                            {formatDate(currentCounty?.coverageEndDate, 'MM/DD/YYYY')}
                        </div>
                    )}
                </div>
            </header>
            
            <div className="px-4 flex gap-4">
                <form
                    className="w-[300px]"
                    name="searchForm"
                    onSubmit={handleSubmit}>
                    
                    <div className="flex gap-2 mb-3 sticky top-0 bg-dark">
                        <button
                            className="btn btn-primary flex-1"
                            type="submit">
                            Search
                        </button>
                        <button
                            className="btn btn-secondary"
                            type="button" onClick={e => handleFormReset(e)}>
                            Clear
                        </button>
                    </div>
                    
                    <SearchFormSidebar
                        activeTab={TAB_SEARCH_INDEX_KEY}
                        handleKeyUp={handleKeyUp}
                        handleChange={handleChange} />
                
                </form>
                <div className="flex-1">
                    <div className={cn('justify-center flex my-6 text-muted', { hidden: !loading })}>
                        <div>Searching...</div>
                        <CircleSpinner className="ml-4 flex" />
                    </div>
                    
                    <div className={cn('flex justify-center my-6 text-muted', {
                        hidden: loading || documents.length,
                    })}>
                        <div>No search results found.</div>
                    </div>
                    
                    {!loading && !!documents?.length && <DocumentCountySearchTable
                        documents={documents ?? []} setDocuments={setDocuments} />}
                </div>
            </div>
        </div>
    )
}
